import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    clientsCompanyResource,
    loadPackagesResource,
    plansResource,
} from "../../helpers/configRoutes/backend_helper";
import CustomButtonOval from "../../components/form/CustomButtonOval";
import FiltersPagination from "../../components/elements/FiltersPagination";
import { currencyUSD } from "../../helpers/forms/formater";
import { useSelector } from "react-redux";
import ConfirmPayment from "./ConfirmPayment";
import { formatPlanLimit, getAiFeatures, getDowngradeAdjustmentMessage, getPlanDowngradeImpact } from "./planFeatureHelpers";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import usePlanUsage from "../../hooks/usePlanUsage";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Subscription = () => {
    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        textLoad: "Processing",
    });

    const [filters, setFilters] = useState({
        perPage: 3,
        page: 1,
        search: "",
    });
    const [meta, setMeta] = useState({ total: 0, current_page: 0 });
    const [plans, setPlans] = useState([]);
    const [loadPackages, setLoadPackages] = useState([]);
    const [company, setCompany] = useState({});
    const user = useSelector((state) => state.Login.user);
    const { features: usageFeatures, getOverLimitFeatures, refresh: refreshUsage } = usePlanUsage();

    const getDataCompany = useCallback(async () => {
        if (!user?.company_id) return;
        const response = await clientsCompanyResource(
            "show",
            {},
            user.company_id
        );
        if (response.status && response.company) {
            setCompany(response.company);
        }
    }, [user?.company_id]);

    useEffect(() => {
        const getPlans = async () => {
            const response = await plansResource("get", { params: filters });
            setPlans(response.plans || []);
            setMeta(response.meta || { total: 0, current_page: 1 });
        };
        getPlans();
    }, [filters]);

    useEffect(() => {
        const getLoadPackages = async () => {
            const response = await loadPackagesResource("get", { params: { perPage: 100, page: 1 } });
            setLoadPackages(response.packetLoads || response.loadPackages || response.packages || []);
        };

        getLoadPackages();
    }, []);

    useEffect(() => {
        getDataCompany();

        const refreshCompany = async () => {
            await Promise.all([getDataCompany(), refreshUsage()]);
        };

        window.addEventListener("company-subscription-updated", refreshCompany);

        return () => {
            window.removeEventListener("company-subscription-updated", refreshCompany);
        };
    }, [getDataCompany, refreshUsage]);

    const isTrialCompany = Boolean(company?.subscription?.is_trial);
    const trialExpired = Boolean(company?.subscription?.trial_expired);
    const trialBanner = company?.subscription?.banner;
    const currentSubscriptionPlanId = company?.lastSubs?.plan_id || company?.plan_id;

    const isCurrentPlan = (plan) => currentSubscriptionPlanId === plan.id;
    const isTrialUpgradePlan = (plan) => isTrialCompany && isCurrentPlan(plan);
    const overLimitFeatures = getOverLimitFeatures();
    const loadFeature = useMemo(() => usageFeatures?.loads || {}, [usageFeatures]);
    const loadUsageSummary = useMemo(() => {
        const used = Number(loadFeature?.used || 0);
        const limit = loadFeature?.limit;

        if (loadFeature?.is_unlimited || limit === null || limit === undefined) {
            return `${used} used / unlimited`;
        }

        const available = Math.max(Number(loadFeature?.available ?? 0), 0);
        return `${used} of ${limit} used · ${available} available`;
    }, [loadFeature]);

    const getActionLabel = (plan) => {
        if (isTrialUpgradePlan(plan)) {
            return trialExpired ? "Activate full subscription" : "Buy full subscription";
        }
        if (isCurrentPlan(plan)) return "Current plan";
        if (!currentSubscriptionPlanId) return "Select plan";
        return currentSubscriptionPlanId < plan.id ? "Upgrade" : "Downgrade";
    };

    return (
        <>
            <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
                <div className="max-w-auto mx-auto space-y-6">
                    {/* HEADER */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <p className="text-sm uppercase tracking-[0.18em] text-cs-muted">
                                Subscription & billing
                            </p>
                            <h1 className="text-2xl font-semibold text-cs-dark">
                                Choose the right plan for your fleet
                            </h1>
                            <p className="text-sm text-cs-muted mt-1">
                                Compare plans, limits and pricing. Your current
                                subscription is highlighted below.
                            </p>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-1 text-sm text-cs-muted">
                            <span>
                                Company:{" "}
                                <span className="font-medium text-cs-primary-dark">
                                    {company.name || "Not configured"}
                                </span>
                            </span>
                            {currentSubscriptionPlanId && (
                                <span>
                                    Active plan ID:{" "}
                                    <span className="font-medium text-cs-primary-dark">
                                        {currentSubscriptionPlanId}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>

                    {trialBanner?.show && (
                        <div className={`rounded-2xl border px-4 py-4 ${trialBanner.is_expired
                            ? "border-amber-300 bg-amber-50"
                            : "border-cs-primary/20 bg-cs-primary-soft"
                            }`}>
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-cs-primary-dark">
                                        Trial version
                                    </p>
                                    <h2 className="text-lg font-semibold text-cs-dark mt-1">
                                        {trialBanner.title}
                                    </h2>
                                    <p className="text-sm text-cs-muted mt-1 max-w-3xl">
                                        {trialBanner.message}
                                    </p>
                                </div>
                                <div className="text-sm font-medium text-cs-primary-dark">
                                    {!trialBanner.is_expired && typeof trialBanner.days_remaining === "number"
                                        ? `${trialBanner.days_remaining} day${trialBanner.days_remaining !== 1 ? "s" : ""} remaining`
                                        : "Choose a full subscription to continue"}
                                </div>
                            </div>
                        </div>
                    )}

                    {overLimitFeatures.length > 0 && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4">
                            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-red-700">
                                        Over limit after downgrade
                                    </p>
                                    <h2 className="text-lg font-semibold text-cs-dark mt-1">
                                        Your current subscription is below your active usage.
                                    </h2>
                                    <p className="text-sm text-cs-muted mt-1 max-w-3xl">
                                        New records are blocked for the affected modules until you reduce usage or move back to a larger plan.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {overLimitFeatures.map((feature) => (
                                        <span
                                            key={feature.feature_key}
                                            className="rounded-full bg-white px-3 py-1 text-xs font-medium text-red-700 border border-red-200"
                                        >
                                            {feature.feature_key === "users" ? "Users" : feature.feature_key === "trucks" ? "Trucks" : feature.feature_key}: {feature.used}/{feature.limit}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PLANS GRID */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {plans.map((plan) => {
                            const price = Number(plan.price || 0);
                            const taxes =
                                price === 0
                                    ? 0
                                    : price *
                                    (Number(plan.taxes || 0) / 100);
                            const total = price + taxes;
                            const aiFeatures = getAiFeatures(plan);
                            const current = isCurrentPlan(plan);
                            const trialUpgrade = isTrialUpgradePlan(plan);
                            const actionLabel = getActionLabel(plan);
                            const downgradeImpact = getPlanDowngradeImpact(plan, usageFeatures);

                            return (
                                <div
                                    key={plan.id}
                                    className={`
                                        relative flex flex-col h-full
                                        rounded-2xl border
                                        p-5
                                        transition-all
                                        ${current
                                            ? "bg-cs-primary-soft border-cs-primary shadow-md shadow-cs-primary/15"
                                            : "bg-cs-bg border-cs-border shadow-sm hover:shadow-md hover:border-cs-primary-soft"
                                        }
                                    `}
                                >
                                    {/* Badge top-right */}
                                    {current && (
                                        <div className={`absolute -top-2 right-3 px-2 py-0.5 rounded-full text-sm font-medium shadow-sm ${trialUpgrade
                                            ? "bg-amber-500 text-white"
                                            : "bg-cs-primary text-white"
                                            }`}>
                                            {trialUpgrade ? "Trial version" : "Current subscription"}
                                        </div>
                                    )}

                                    {/* Plan name & price */}
                                    <div className="mb-3">
                                        <h3 className="text-sm font-semibold text-cs-dark">
                                            {plan.name}
                                        </h3>
                                        <div className="flex items-baseline gap-1 mt-1">
                                            <span className="text-2xl font-bold text-cs-primary">
                                                {currencyUSD(price)}
                                            </span>
                                            <span className="text-sm text-cs-muted">
                                                / month
                                            </span>
                                        </div>
                                        {price === 0 && (
                                            <p className="text-sm text-cs-success mt-0.5">
                                                Trial / Free tier
                                            </p>
                                        )}
                                        {trialUpgrade && (
                                            <p className="text-sm text-amber-600 mt-0.5">
                                                You are currently on trial for this plan.
                                            </p>
                                        )}
                                    </div>

                                    <div className="h-px bg-cs-border/60 my-2" />

                                    {/* Details */}
                                    <div className="flex-1 mt-1 space-y-1.5 text-sm text-cs-muted">
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Base:
                                            </span>{" "}
                                            {currencyUSD(price)} USD
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Taxes:
                                            </span>{" "}
                                            {currencyUSD(
                                                price === 0 ? 0 : taxes
                                            )}
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Est. total:
                                            </span>{" "}
                                            {currencyUSD(
                                                price === 0 ? 0 : total
                                            )}{" "}
                                            / month
                                        </p>
                                        <p className="mt-1">
                                            <span className="font-medium text-cs-dark">
                                                Trucks included:
                                            </span>{" "}
                                            {formatPlanLimit(plan.trucks)}
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Users included:
                                            </span>{" "}
                                            {formatPlanLimit(plan.users)}
                                        </p>

                                        {downgradeImpact.hasImpact && (
                                            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
                                                    Adjustment required before downgrade
                                                </p>
                                                <p className="mt-1 text-xs text-cs-muted">
                                                    {getDowngradeAdjustmentMessage(downgradeImpact.impacted)}
                                                </p>
                                                <p className="mt-1 text-xs text-cs-muted">
                                                    You will not be able to confirm this change until usage fits within the new plan.
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {downgradeImpact.impacted.map((item) => (
                                                        <span
                                                            key={item.key}
                                                            className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-amber-700 border border-amber-200"
                                                        >
                                                            {item.label}: {item.used}/{item.limit}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
 
                                        <div className="mt-3 rounded-xl border border-cs-border/80 bg-white/70 p-3">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cs-primary-dark">
                                                    AI included
                                                </p>
                                                <span className="text-[11px] text-cs-muted">
                                                    {aiFeatures.included.length} feature{aiFeatures.included.length !== 1 ? "s" : ""}
                                                </span>
                                            </div>

                                            <div className="mt-2 space-y-2">
                                                {aiFeatures.all.map((feature) => (
                                                    <div
                                                        key={feature.key}
                                                        className={`rounded-lg border px-3 py-2 ${feature.enabled
                                                            ? "border-cs-primary/20 bg-cs-primary-soft/40"
                                                            : "border-cs-border bg-cs-bg-alt/70 opacity-75"
                                                            }`}
                                                    >
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div>
                                                                <p className="text-sm font-medium text-cs-dark">
                                                                    {feature.label}
                                                                </p>
                                                                <p className="text-[11px] text-cs-muted mt-0.5">
                                                                    {feature.description}
                                                                </p>
                                                            </div>
                                                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${feature.enabled
                                                                ? "bg-cs-primary text-white"
                                                                : "bg-cs-border text-cs-muted"
                                                                }`}>
                                                                {feature.enabled ? "Included" : "Not included"}
                                                            </span>
                                                        </div>

                                                        {feature.enabled && (
                                                            <p className="mt-2 text-[11px] font-medium text-cs-primary-dark">
                                                                {feature.limit}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="h-px bg-cs-border/60 my-3" />

                                    {/* Action button */}
                                    <div className="mt-auto flex flex-col items-center gap-1">
                                        <CustomButtonOval
                                            onClick={() =>
                                                (trialUpgrade || !current) &&
                                                plan?.can?.changeSubs &&
                                                setModalConfirm({
                                                    ...modalConfirm,
                                                    show: true,
                                                    plan,
                                                    company,
                                                    usageFeatures,
                                                    downgradeImpact,
                                                })
                                            }
                                            isDisabled={
                                                !plan?.can?.changeSubs || (current && !trialUpgrade)
                                            }
                                            className={`
                                                w-full text-[10px] font-medium
                                                ${current && !trialUpgrade
                                                    ? "bg-cs-primary text-white cursor-default"
                                                    : plan?.can?.changeSubs
                                                        ? "bg-cs-bg text-cs-primary border border-cs-primary hover:bg-cs-primary hover:text-white transition-all"
                                                        : "bg-cs-bg-alt text-cs-muted border border-cs-border cursor-not-allowed"
                                                }
                                            `}
                                        >
                                            {actionLabel}
                                        </CustomButtonOval>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination (sin filtros visuales aquí) */}
                    <FiltersPagination
                        filters={filters}
                        setFilters={setFilters}
                        showFilters={false}
                        meta={meta}
                    />

                    <div className="rounded-2xl border border-cs-border bg-cs-bg p-5 shadow-sm">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="text-sm uppercase tracking-[0.18em] text-cs-muted">
                                    Extra load packages
                                </p>
                                <h2 className="text-xl font-semibold text-cs-dark mt-1">
                                    Add more loads without changing your plan
                                </h2>
                                <p className="text-sm text-cs-muted mt-1 max-w-3xl">
                                    These packages increase your available loads on the active subscription and are billed as one-time purchases.
                                </p>
                            </div>
                            <div className="rounded-2xl bg-cs-primary-soft px-4 py-3 text-sm text-cs-primary-dark">
                                <p className="font-semibold">Current loads usage</p>
                                <p>{loadUsageSummary}</p>
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {loadPackages.map((pack) => {
                                const price = Number(pack.price || 0);
                                const taxes = (price * Number(pack.taxes || 0)) / 100;
                                const total = price + taxes;

                                return (
                                    <div
                                        key={pack.id}
                                        className="rounded-2xl border border-cs-border bg-cs-bg-alt p-5 shadow-sm hover:shadow-md transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-cs-dark">{pack.name}</h3>
                                                <p className="text-sm text-cs-muted mt-1">
                                                    {pack.description || "One-time package to extend your load capacity."}
                                                </p>
                                            </div>
                                            <span className="rounded-full bg-cs-primary-soft px-3 py-1 text-xs font-semibold text-cs-primary-dark">
                                                +{pack.loads || 0} loads
                                            </span>
                                        </div>

                                        <div className="mt-4 space-y-2 text-sm text-cs-muted">
                                            <p><span className="font-medium text-cs-dark">Base:</span> {currencyUSD(price)}</p>
                                            <p><span className="font-medium text-cs-dark">Taxes:</span> {currencyUSD(taxes)}</p>
                                            <p><span className="font-medium text-cs-dark">Total:</span> {currencyUSD(total)}</p>
                                            <p><span className="font-medium text-cs-dark">Validity:</span> {pack.validity_days || 0} day{Number(pack.validity_days || 0) !== 1 ? "s" : ""}</p>
                                        </div>

                                        <div className="mt-5">
                                            <CustomButtonOval
                                                onClick={() =>
                                                    pack?.can?.changeSubs &&
                                                    setModalConfirm({
                                                        ...modalConfirm,
                                                        show: true,
                                                        package: pack,
                                                        checkoutType: "load-package",
                                                        company,
                                                        usageFeatures,
                                                    })
                                                }
                                                isDisabled={!pack?.can?.changeSubs}
                                                className={`w-full text-[10px] font-medium ${pack?.can?.changeSubs
                                                    ? "bg-cs-bg text-cs-primary border border-cs-primary hover:bg-cs-primary hover:text-white transition-all"
                                                    : "bg-cs-bg-alt text-cs-muted border border-cs-border cursor-not-allowed"
                                                    }`}
                                            >
                                                Buy load package
                                            </CustomButtonOval>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            {modalConfirm.show &&
                <Elements stripe={stripePromise}>
                    <ConfirmPayment
                        modal={modalConfirm}
                        setModal={setModalConfirm}
                    />
                </Elements>
            }
        </>
    );
};

export default Subscription;
